// src/components/IncomingOrders/OrderDetail.jsx

import { saveAs } from "file-saver";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useMutation } from "@apollo/client";
import { useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Fade from "@mui/material/Fade";
import Grid from "@mui/material/Grid";
import { makeStyles } from "@mui/styles";
import { INITIALIZE_ORDER_HISTORY_DOWNLOAD } from "../../graphql/mutations";
import { createAddressFieldPath, getAddressObjectName } from "./helpers/addressUtils";
import AddressCompact from "./AddressCompact";
import AddressCompareTable from "./AddressCompareTable";
import AddressEditDialog from "./AddressEditDialog";
import AddressInfo from "./AddressInfo";
import OrderMetaCompact from "./OrderMetaCompact";
import OrderProductsAndSummation from "./OrderProductsAndSummation";

const useStyles = makeStyles((theme) => ({
  modalBody: {
    backgroundColor: theme.palette.common.white,
    "@media print": {
      margin: 0,
      padding: 0,
    },
  },
  title: {
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 10,
    padding: "6px 0",
    textTransform: "uppercase",
  },
  divider: {
    margin: "20px 0",
    "@media print": {
      margin: "5px 0",
    },
  },
  spacing: {
    margin: "10px 0",
  },
  text: {
    fontSize: 12,
  },
  orderInformation: {
    "@media print": {
      display: "inline-block !important",
      verticalAlign: "top",
      width: "50%",
    },
  },
  addressRow: {
    "@media print": {
      display: "block",
      width: "100%",
      margin: 0,
      padding: 0,
      position: "relative",
      verticalAlign: "top",
    },
  },
  addressContainer: {
    "@media print": {
      padding: 0,
      margin: 0,
      position: "relative",
      display: "inline-block !important",
      width: "50%",
      verticalAlign: "top",
    },
  },
  addressArea: {
    // avtoning för att ordertabellen ska dominera visuellt
    backgroundColor: "rgba(0,0,0,0.008)",
    borderRadius: 2,
    padding: 8,
  },
  addressCompareWrap: {
    marginTop: 8,
  },
  screenOnly: {
    "@media print": { display: "none" },
  },
  printOnly: {
    display: "none",
    "@media print": { display: "block" },
  },
}));

const OrderDetail = ({
  order,
  settings = {},
  width,
  addressView,
  setAddressView,
  updateField,
  isFieldDirty,
  isObjectDirty,
  getAddressLabel,
  onOpenStatusManager,
  availableUsers,
  currentSelectedUser,
  setCurrentSelectedUser,
  tableSettings,
  panelWidth,
}) => {
  const classes = useStyles();
  const intl = useIntl();

  const {
    comment,
    costPlaceOrder,
    deliveryDateWish,
    orderRows,
  } = order || {};

  const updateAddressField = (type, field, value) => {
    //const fieldPath = createAddressFieldPath(type, field);   

    debugger;
    updateField(null, value, {
      section: type,
      field: field
    });
  };

  // --- Compact edit dialog state ---
  const [editAddressFields, setEditAddressFields] = useState(null);
  const [editAddressType, setEditAddressType] = useState(null); // "delivery" | "invoice" | "orderer"
  const [editAddressDialogOpen, setEditAddressDialogOpen] = useState(false);

  const openEditAddressDialog = (type) => {
    const addressObjectName = getAddressObjectName(type);

    if (!addressObjectName)
      return;

    const addressFields = order.sortedAddressFields?.[addressObjectName] || [];

    setEditAddressFields(addressFields);

    //setEditAddress(address);
    setEditAddressType(type);
    setEditAddressDialogOpen(true);
  };

  const closeEditAddressDialog = () => {
    setEditAddressFields(null);
    setEditAddressType(null);
    setEditAddressDialogOpen(false);
  };

  const handleUpdateAddress = (addressType, changedFields) => {
    changedFields.forEach(({ field, value }) => {
      updateAddressField(addressType, field, value);
    });

    closeEditAddressDialog();
  };

  // Ny: 3 vyer istället för showFullAddresses
  const LS_KEY = "orderAddressView";

  // const [addressView, setAddressView] = useState(() => {
  //   try {
  //     const v = localStorage.getItem(LS_KEY);
  //     // bakåtkompatibelt: gammalt värde "overview" ska bli "compact"
  //     if (v === "overview") return "compact";
  //     return v || "compact";
  //   } catch (e) {
  //     return "compact";
  //   }
  // });

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, addressView);
    } catch (e) {
      // t.ex. blocked storage / private mode
    }
  }, [addressView]);

  const [initializeDownload] = useMutation(INITIALIZE_ORDER_HISTORY_DOWNLOAD);
  const isMobile = useMediaQuery("(max-width: 900px)");

  //const isSmall = (width < 800) && !isMobile;
  //const md = isSmall ? 12 : 6;
  const md = 6;

  const hasAddress = (addr) =>
    Object.values(addr || {}).some(v => v && String(v).trim() !== "");

  const {
    showComment = !!comment,
    showCostCenter = !!costPlaceOrder,
    showDeliveryAddress = hasAddress(order.deliveryAddress),
    showInvoiceAddress = hasAddress(order.invoiceAddress),
    showDeliveryDate = !!deliveryDateWish,
    showEmail = !!(order?.ordererAddress?.email),
    showOrderStatus = true,
    showConfirmedDeliveryDateColumn = true,
    showDeliveryDateWishColumn = true,
    showDownloadColumn = !true,  // TODO: showDownloadColumn
    showPackageColumn = false,
    showPostalAddress = true,
    showPriceColumn = true,
    showStatusColumn = true,
    showProductColumn = true,
    showQuantityColumn = true,
    showHandledByColumn = true,
    showStockLocation = true,
    showSummation = true,
    showWeightColumn = !!(
      Array.isArray(orderRows) && orderRows.some((r) => r.weight)
    ),
  } = settings || {};

  const resolvedSettings = {
    ...settings,
    showComment,
    showCostCenter,
    showEmail,
    showDeliveryDate,
    showOrderStatus,
  };

  const handleDownloadOrderFile = async (orderRowId) => {
    try {
      const results = await initializeDownload({
        variables: { orderRowId },
      });
      if (results && results.data && results.data.initOrderFileDownload) {
        const downloadUrl = results.data.initOrderFileDownload;
        saveAs(downloadUrl);
      } else {
        console.log("** Download Failure");
      }
    } catch (e) {
      console.log(`** Error downloading order history ${e}`);
    }
  };

  if (!order) {
    return <div>Loading...</div>;
  }

  return (
    <div className={classes.modalBody}>
      <Grid container spacing={2} className={classes.addressRow}>
        <Grid item xs={12}>
          <Box className={classes.addressArea}>
            {/* ===== Screen (3 vyer) ===== */}
            <Box className={classes.screenOnly}>

              {/* 1) Översikt: kompakta kort + meta (din nuvarande layout) */}
              <Fade in={addressView === "compact"} timeout={220} mountOnEnter unmountOnExit>
                <Box>
                  <Grid container spacing={1} alignItems="stretch">
                    {showDeliveryAddress && (
                      <Grid
                        item
                        xs={12}
                        md={md}
                        className={classes.addressContainer}
                        sx={{ display: "flex" }}
                      >
                        <AddressCompact
                          title={intl.formatMessage({
                            id: "orderHistory.deliveryAddress",
                          })}
                          addressFields={order?.sortedAddressFields?.deliveryAddress}
                          showToggle={false}
                          onEdit={() => openEditAddressDialog("deliveryAddress")}
                          enableClickToEdit
                          dirty={isObjectDirty("deliveryAddress")}
                        />
                      </Grid>
                    )}

                    {showInvoiceAddress && (
                      <Grid
                        item
                        xs={12}
                        md={md}
                        className={classes.addressContainer}
                        sx={{ display: "flex" }}
                      >
                        <AddressCompact
                          title={intl.formatMessage({
                            id: "orderHistory.invoiceAddress",
                          })}
                          addressFields={order?.sortedAddressFields?.invoiceAddress}
                          showToggle={false}
                          onEdit={() => openEditAddressDialog("invoiceAddress")}
                          enableClickToEdit
                          dirty={isObjectDirty("invoiceAddress")}
                        />
                      </Grid>
                    )}

                    <Grid
                      item
                      xs={12}
                      md={md}
                      className={classes.addressContainer}
                      sx={{ display: "flex" }}
                    >
                      <AddressCompact
                        title={intl.formatMessage({
                          id: "orderHistory.ordererAddress",
                        })}
                        addressFields={order?.sortedAddressFields?.ordererAddress}
                        showToggle={false}
                        onEdit={() => openEditAddressDialog("ordererAddress")}
                        enableClickToEdit
                        dirty={isObjectDirty("ordererAddress")}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={md}
                      className={classes.addressContainer}
                      sx={{ display: "flex" }}
                    >
                      <OrderMetaCompact
                        variant="compact"
                        order={order}
                        settings={resolvedSettings}
                        updateField={updateField}
                        isFieldDirty={isFieldDirty}
                        availableUsers={availableUsers}
                        currentSelectedUser={currentSelectedUser}
                        setCurrentSelectedUser={setCurrentSelectedUser}
                        panelWidth={panelWidth}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Fade>

              {/* 2) Jämför: ersätter hela adresskorten */}
              <Fade in={addressView === "compare"} timeout={220} mountOnEnter unmountOnExit>
                <Box className={classes.addressCompareWrap}>
                  <AddressCompareTable
                    title=""
                    editable
                    isFieldDirty={isFieldDirty}
                    onChangeCell={(columnId, field, value) => {

                      updateAddressField(columnId, field, value);
                    }}
                    columns={[
                      showDeliveryAddress
                        ? {
                          editType: "deliveryAddress",
                          title: intl.formatMessage({ id: "orderHistory.deliveryAddress" }),
                          address: order.sortedAddressFields?.deliveryAddress,
                          getAddressLabel: (field, fallback) => getAddressLabel("deliveryAddress", field, fallback),
                        }
                        : null,
                      showInvoiceAddress
                        ? {
                          editType: "invoiceAddress",
                          title: intl.formatMessage({ id: "orderHistory.invoiceAddress" }),
                          address: order.sortedAddressFields?.invoiceAddress,
                          getAddressLabel: (field, fallback) => getAddressLabel("invoiceAddress", field, fallback),
                        }
                        : null,
                      {
                        editType: "ordererAddress",
                        title: intl.formatMessage({ id: "orderHistory.ordererAddress" }),
                        address: order.sortedAddressFields?.ordererAddress,
                        getAddressLabel: (field, fallback) => getAddressLabel("ordererAddress", field, fallback),
                      },
                    ].filter(Boolean)}
                  />
                </Box>
              </Fade>

              {/* 3) Detaljer: tre separata AddressInfo-kolumner (ordning kan skilja sig per adress) */}
              <Fade in={addressView === "full"} timeout={220} mountOnEnter unmountOnExit>
                <Box>
                  <Grid container spacing={0.5} alignItems="stretch">
                    {showDeliveryAddress && (
                      <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                        <Box
                          sx={{
                            flex: 1,
                            borderRadius: 2,
                            backgroundColor: "rgba(0,0,0,0.03)",
                            overflow: "hidden",
                          }}
                        >
                          <AddressInfo
                            tableTitle={intl.formatMessage({ id: "orderHistory.deliveryAddress" })}
                            address={order?.sortedAddressFields?.deliveryAddress}
                            getAddressLabel={(field, fallback) => getAddressLabel("deliveryAddress", field, fallback)}
                            editable
                            isFieldDirty={(key) => isFieldDirty(createAddressFieldPath("deliveryAddress", key))}
                            onChange={(field, value) => updateAddressField("deliveryAddress", field, value)}
                          />
                        </Box>
                      </Grid>
                    )}

                    {showInvoiceAddress && (
                      <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                        <Box
                          sx={{
                            flex: 1,
                            borderRadius: 2,
                            backgroundColor: "rgba(0,0,0,0.03)",
                            overflow: "hidden",
                          }}
                        >
                          <AddressInfo
                            tableTitle={intl.formatMessage({ id: "orderHistory.invoiceAddress" })}
                            address={order?.sortedAddressFields?.invoiceAddress}
                            getAddressLabel={(field, fallback) => getAddressLabel("invoiceAddress", field, fallback)}
                            editable
                            isFieldDirty={(key) => isFieldDirty(createAddressFieldPath("invoiceAddress", key))}
                            onChange={(field, value) => updateAddressField("invoiceAddress", field, value)}
                          />
                        </Box>
                      </Grid>
                    )}

                    <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          flex: 1,
                          borderRadius: 2,
                          backgroundColor: "rgba(0,0,0,0.03)",
                          overflow: "hidden",
                        }}
                      >
                        <AddressInfo
                          tableTitle={intl.formatMessage({ id: "orderHistory.ordererAddress" })}
                          address={order?.sortedAddressFields?.ordererAddress}
                          getAddressLabel={(field, fallback) => getAddressLabel("ordererAddress", field, fallback)}
                          editable
                          isFieldDirty={(key) => isFieldDirty(createAddressFieldPath("ordererAddress", key))}
                          onChange={(field, value) => updateAddressField("ordererAddress", field, value)}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Fade>

              {/* META – synlig i 2 lägen av tre*/}
              {(addressView === "compare" || addressView === "full") && (
                <Box
                  mt={2}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <OrderMetaCompact
                    variant="wide"
                    order={order}
                    settings={resolvedSettings}
                    updateField={updateField}
                    isFieldDirty={isFieldDirty}
                    availableUsers={availableUsers}
                    currentSelectedUser={currentSelectedUser}
                    setCurrentSelectedUser={setCurrentSelectedUser}
                    tableSettings={tableSettings}
                    panelWidth={panelWidth}
                  />
                </Box>
              )}

            </Box>

            {/* ===== Print: behåll din gamla printvy oförändrad ===== */}
            <Box className={classes.printOnly}>
              <Grid container spacing={2}>
                {showDeliveryAddress && (
                  <Grid item xs={4}>
                    <AddressInfo
                      tableTitle={intl.formatMessage({ id: "orderHistory.deliveryAddress" })}
                      address={order?.sortedAddressFields?.deliveryAddress}
                      getAddressLabel={(field, fallback) => getAddressLabel("deliveryAddress", field, fallback)}
                      section="deliveryAddress"
                      mode="print"
                    />
                  </Grid>
                )}

                {showInvoiceAddress && (
                  <Grid item xs={4}>
                    <AddressInfo
                      tableTitle={intl.formatMessage({ id: "orderHistory.invoiceAddress" })}
                      address={order?.sortedAddressFields?.invoiceAddress}
                      section="invoiceAddress"
                      getAddressLabel={(field, fallback) => getAddressLabel("invoiceAddress", field, fallback)}
                      mode="print"
                    />
                  </Grid>
                )}

                <Grid item xs={4}>
                  <AddressInfo
                    tableTitle={intl.formatMessage({ id: "orderHistory.ordererAddress" })}
                    address={order?.sortedAddressFields?.ordererAddress}
                    section="ordererAddress"
                    getAddressLabel={(field, fallback) => getAddressLabel("ordererAddress", field, fallback)}
                    mode="print"
                  />
                </Grid>
              </Grid>
            </Box>

          </Box>
        </Grid>
      </Grid>

      <Divider className={classes.divider} />

      <OrderProductsAndSummation
        isEditing // TODO

        isFieldDirty={isFieldDirty}
        updateField={updateField}

        onDownloadOrderFile={handleDownloadOrderFile}
        onOpenStatusManager={onOpenStatusManager}
        orderRows={order?.orderRows ?? []}
        supplierOrderStatuses={order?.supplierOrderStatuses ?? []}
        sumExclVat={order.sumExclVat}
        sumInclVat={order.sumInclVat}
        transportFee={order.transportFee}
        vatAmounts={order.vatAmounts}

        showConfirmedDeliveryDateColumn={showConfirmedDeliveryDateColumn}
        showDeliveryDateWishColumn={showDeliveryDateWishColumn}
        showDownloadColumn={showDownloadColumn}
        showPackageColumn={showPackageColumn}
        showPriceColumn={showPriceColumn}
        showStatusColumn={showStatusColumn}
        showProductColumn={showProductColumn}
        showQuantityColumn={showQuantityColumn}
        showHandledByColumn={showHandledByColumn}
        showStockLocation={showStockLocation}
        showSummation={showSummation}
        showWeightColumn={showWeightColumn}
        currency={order.currencyCode}

        availableUsers={availableUsers}
        currentSelectedUser={currentSelectedUser}
        setCurrentSelectedUser={setCurrentSelectedUser}
        tableSettings={tableSettings}
      />


      {editAddressDialogOpen && (
        <AddressEditDialog
          addressFields={editAddressFields}
          addressType={editAddressType}
          isFieldDirty={(key) =>
            isFieldDirty(createAddressFieldPath(editAddressType, key))
          }
          createAddressFieldPath={createAddressFieldPath}
          updateField={updateAddressField}
          onClose={closeEditAddressDialog}
          onSave={handleUpdateAddress}
          open={editAddressDialogOpen}
        />
      )}

    </div>
  );
};

OrderDetail.propTypes = {
  settings: PropTypes.shape({
    showComment: PropTypes.bool,
    showCostCenter: PropTypes.bool,
    showDeliveryAddress: PropTypes.bool,
    showDeliveryDate: PropTypes.bool,
    showEmail: PropTypes.bool,
    showInvoiceAddress: PropTypes.bool,
    showOrderStatus: PropTypes.bool,
    showPackageColumn: PropTypes.bool,
    showPostalAddress: PropTypes.bool,
    showPriceColumn: PropTypes.bool,
    showProductColumn: PropTypes.bool,
    showQuantityColumn: PropTypes.bool,
    showSummation: PropTypes.bool,
    showWeightColumn: PropTypes.bool,
  }),
  order: PropTypes.shape({
    comment: PropTypes.string,
    costPlaceOrder: PropTypes.string,
    deliveryAddress: PropTypes.shape({
      address1: PropTypes.string,
      address2: PropTypes.string,
      city: PropTypes.string,
      contact: PropTypes.string,
      country: PropTypes.string,
      email: PropTypes.string,
      reference: PropTypes.string,
      telephone: PropTypes.string,
      zip: PropTypes.string,
    }),
    invoiceAddress: PropTypes.shape({
      address1: PropTypes.string,
      address2: PropTypes.string,
      city: PropTypes.string,
      contact: PropTypes.string,
      country: PropTypes.string,
      email: PropTypes.string,
      reference: PropTypes.string,
      telephone: PropTypes.string,
      zip: PropTypes.string,
    }),
    ordererAddress: PropTypes.shape({
      email: PropTypes.string,
    }),
    deliveryDateWish: PropTypes.string,
    orderDate: PropTypes.string,
    orderId: PropTypes.number,
    status: PropTypes.string,
    orderRows: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        amount: PropTypes.number,
        price: PropTypes.number,
        product: PropTypes.string,
        weight: PropTypes.number,
        confirmedDeliveryDate: PropTypes.string,
        deliveryDateWish: PropTypes.string,
      })
    ),
  }).isRequired,
  panelWidth: PropTypes.number,
};

OrderDetail.defaultProps = {
  settings: {},
};

OrderDetail.displayName = "OrderDetail";

export default OrderDetail;
